// src/admin/components/workflows/WorkflowsTable

import React from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/common/DataTable';
import DateTimeDisplay from '../../components/common/DateTimeDisplay';
import { WorkflowTypeBadges } from '../../../shared/components/WorkflowTypeBadge';
import WorkflowLifecycleBadge from '../../../shared/components/WorkflowLifecycleBadge';
import TagChipList from '../tags/TagChipList';

/** Prefer editing the pending draft when one exists for a published row. */
function editWorkflowId(row) {
  if (row.draft_workflow_id) return row.draft_workflow_id;
  return row.id;
}

export default function WorkflowsTable({ items, filterExtras = null, tags = [] }) {
  const navigate = useNavigate();
  const tagsById = Object.fromEntries(tags.map((t) => [t.id, t]));

  const columns = [
    {
      key: 'name',
      label: 'Workflow',
      sortable: true,
      render: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-start text-decoration-none"
          onClick={() => navigate(`/admin/flow/${editWorkflowId(row)}/edit`)}
        >
          <i className="bi bi-diagram-2 text-primary me-2" />
          <span className="fw-semibold">{row.name}</span>
          <WorkflowTypeBadges workflow={row} className="ms-2" />
        </button>
      ),
      filterValue: (row) =>
        `${row.name} ${row.description || ''} ${row.user_workflow ? 'user workflow' : ''} ${
          row.self_startable ? 'self startable' : ''
        }`,
    },
    {
      key: 'lifecycle',
      label: 'Lifecycle',
      sortable: true,
      render: (row) => {
        const showDraftBadge = row.has_pending_draft;

        return (
          <div className="d-flex align-items-center gap-1 flex-wrap">
            <span className="text-capitalize small text-muted">{row.state || '—'}</span>
            {showDraftBadge && <WorkflowLifecycleBadge variant="pending_draft" />}
          </div>
        );
      },
      filterValue: (row) =>
        `${row.state || ''} ${row.has_pending_draft ? 'draft' : ''}`,
    },
    {
      key: 'published_at',
      label: 'Last published',
      sortable: true,
      render: (row) => (
        <span className="text-muted small">
          <DateTimeDisplay isoString={row.published_at} />
        </span>
      ),
      filterValue: (row) => row.published_at || '',
    },
    {
      key: 'updated_at',
      label: 'Last updated',
      sortable: true,
      render: (row) => (
        <span className="text-muted small">
          <DateTimeDisplay isoString={row.updated_at} />
        </span>
      ),
      filterValue: (row) => row.updated_at || '',
    },
    {
      key: 'tags',
      label: 'Tags',
      render: (row) => {
        const rowTags = (row.tag_ids || []).map((id) => tagsById[id]).filter(Boolean);
        if (rowTags.length === 0) {
          return <span className="text-muted small fst-italic">Not scoped</span>;
        }
        return <TagChipList tags={rowTags} />;
      },
      filterValue: (row) =>
        (row.tag_ids || []).map((id) => tagsById[id]?.name).filter(Boolean).join(' '),
    },
  ];

  const actions = [
    {
      key: 'view',
      label: 'View',
      icon: 'bi-eye',
      onClick: (row) => {
        window.open(`/admin/flow/${row.id}/preview`, '_blank', 'noopener,noreferrer');
      },
    },
    {
      key: 'edit',
      label: 'Edit',
      icon: 'bi-pencil',
      onClick: (row) => {
        window.location.href = `/admin/flow/${editWorkflowId(row)}/edit`;
      },
    },
  ];

  return (
    <DataTable
      items={items}
      columns={columns}
      actions={actions}
      filterExtras={filterExtras}
      filterPlaceholder="Search workflows…"
      emptyMessage="No workflows match your filters."
    />
  );
}
